import { StrictMode, useCallback, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./App.js";
import {
  chargerLangue, coffreNavigateur, sauverLangue,
} from "./campagne/coffre.js";
import { FournisseurDeLangue } from "./i18n/contexte.js";
import type { Langue } from "./i18n/index.js";
import "./styles.css";

/** Le coffre, monté une fois : IndexedDB ouvre une base, pas à chaque rendu. */
const coffre = coffreNavigateur();

/**
 * La racine : elle ne fait qu'une chose, décider dans quelle langue on affiche.
 *
 * Le coffre est asynchrone, donc le tout premier rendu se fait dans la langue du
 * NAVIGATEUR. C'est le bon repli : quelqu'un qui n'a jamais rien choisi n'attend rien
 * d'autre, et celui qui a choisi voit son choix arriver au rendu suivant, avant même
 * d'avoir lu la première ligne.
 *
 * ⚠️ `null` tant qu'on ne sait pas, et non « français » : écrire une valeur par défaut
 * figerait le choix pour toujours, y compris chez quelqu'un qui change ensuite la
 * langue de son téléphone.
 */
function Racine() {
  const [retenue, setRetenue] = useState<Langue | null>(null);

  useEffect(() => {
    let vivant = true;
    void chargerLangue(coffre).then((langue) => {
      if (vivant && langue) setRetenue(langue);
    });
    return () => { vivant = false; };
  }, []);

  const retenir = useCallback((langue: Langue) => {
    void sauverLangue(coffre, langue);
  }, []);

  return (
    <FournisseurDeLangue depart={retenue ?? undefined} onChangement={retenir}>
      <App />
    </FournisseurDeLangue>
  );
}

const racine = document.getElementById("racine");
if (!racine) throw new Error("élément #racine introuvable");
createRoot(racine).render(<StrictMode><Racine /></StrictMode>);

/**
 * Le mode hors-ligne.
 *
 * Le service worker est **engendré au build** (`v2/tools/generer-sw.mjs`) parce que
 * les noms des assets portent un hachage qui change à chaque version : un fichier
 * écrit à la main deviendrait faux au build suivant, et silencieusement — il ne
 * trouverait simplement plus rien en cache.
 *
 * ⚠️ **Seulement en production.** En développement, Vite sert les sources depuis
 * `/src` et il n'y a pas de `sw.js` ; le serveur renvoyant `index.html` pour toute
 * route inconnue, le navigateur refusait le script avec « unsupported MIME type
 * ('text/html') » à chaque chargement. C'est ce qui avait fait retirer ces lignes.
 */
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").then((enregistrement) => {
      // Le préchauffage réclame le gros morceau — la physique et les tables d'IA —
      // une fois la page affichée. Sans lui, « installer puis partir dans le métro »
      // ne marcherait qu'après avoir joué une partie une première fois. Avec un
      // délai : au chargement, le réseau sert encore la scène que le joueur regarde.
      setTimeout(() => {
        (enregistrement.active ?? navigator.serviceWorker.controller)
          ?.postMessage({ type: "prechauffer" });
      }, 4000);
    }).catch(() => {
      // Un service worker refusé n'empêche pas de jouer : le jeu marche en ligne
      // exactement comme avant. On ne crie pas dans la console pour autant.
    });
  });
}
